import { Box, createStyles } from '@mantine/core'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'

import { CarouselImage as CarouselImageType } from '@/types/image'

import CarouselThumb from './CarouselThumb'
import ImageZoom from './ImageZoom'

interface Props {
  slides?: CarouselImageType[]
}

const useStyles = createStyles(theme => ({
  thumbs: {
    '& .embla__slide__inner': {
      border: '1px solid #CCCCCC',
      borderRadius: '5px',
      overflow: 'hidden',
      padding: 0
    }
  },
  viewport: {
    maxHeight: '480px',
    overflow: 'hidden'
  },
  container: {
    display: 'flex'
  },
  slide: {
    minWidth: '100%',
    position: 'relative'
  },
  slideInner: {
    position: 'relative',
    overflow: 'hidden'
  }
}))

export default function ImagesCarousel({ slides }: Props) {
  const { classes } = useStyles()
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [mainViewportRef, embla] = useEmblaCarousel({ skipSnaps: false })
  const [thumbViewportRef, emblaThumbs] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true
  })

  const onThumbClick = useCallback(
    (index: number) => {
      if (!slides || !embla || !emblaThumbs) return
      setSelectedIndex(index)
      if (emblaThumbs.clickAllowed()) embla.scrollTo(index)
    },
    [slides, embla, emblaThumbs]
  )

  const onSelect = useCallback(() => {
    if (!slides || !embla || !emblaThumbs) return
    setSelectedIndex(embla.selectedScrollSnap())
    emblaThumbs.scrollTo(embla.selectedScrollSnap())
  }, [slides, embla, emblaThumbs, setSelectedIndex])

  useEffect(() => {
    if (!embla) return
    onSelect()
    embla.on('select', onSelect)
  }, [embla, onSelect])

  return slides ? (
    <>
      <Box mb="md">
        <div className={classes.viewport} ref={mainViewportRef}>
          <div className={classes.container}>
            {slides?.map((productImage: CarouselImageType, index) => (
              <div className={classes.slide} key={productImage.file}>
                <div className={classes.slideInner}>
                  <ImageZoom image={productImage} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </Box>

      <Box hidden={slides.length <= 1} className={classes.thumbs}>
        <div className={classes.viewport} ref={thumbViewportRef}>
          <div className={classes.container} style={{ margin: '0 -5px' }}>
            {slides?.map((item: CarouselImageType, index) => (
              <CarouselThumb
                onClick={() => onThumbClick(index)}
                selected={index === selectedIndex}
                imgSrc={item.file}
                key={index}
              />
            ))}
          </div>
        </div>
      </Box>
    </>
  ) : (
    <></>
  )
}
